/**
 * Set 3 — subscription card with start-edge plan image (flush on 3 sides)
 * and Set1-style metrics. When C/P + calorie range are hidden, meals/snacks
 * and remaining-days tags share one full-width row.
 */
import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, Image, Platform } from 'react-native';
import { useTranslation, getSubscriptionPlanTitleFromMerged, formatNumber } from '../../../../shared/i18n';
import {
  useShowSubscriptionScaledMacrosEnabled,
  useCaloriesLimitVisible,
  usePortionTextMode,
  useShowPortionEnabled,
} from '../../../../data/domains/config';
import {
  formatMealDaysRemainingLabel,
  formatPersonCountLabel,
  resolveMergedSubscriptionImageUri,
  resolvePurchasedSubscriptionCardNutrition,
  resolveSubscriptionCardNutritionDisplay,
  useSubscriptionPlans,
  type SubscriptionData,
} from '../../../../data/domains/subscription';
import { useRtl } from '../../../../shared/i18n/useRtl';
import { horizontalLtrDirectionStyle } from '../../../../shared/i18n/rtl';
import { appTheme } from '../../../../shared/theme';
import type { ThemeColors } from '../../../../shared/theme';
import { hexToRgba } from '../../../../shared/theme/colorUtils';
import { Clickable } from '../../../../components/common/Clickable';
import { SvgIcon } from '../../../../components/common/SvgIcon';
import { dashboardSet1CardShell } from '../set1/dashboardSet1Styles';
import { DASH_STUDIO_INNER, DASH_STUDIO_RADIUS } from '../../../../shared/theme/studio/dashboardConstants';
import type { FlatItem } from '../../DashboardMenuImagesStrip';

const ANDROID_TEXT = Platform.OS === 'android' ? { includeFontPadding: false } : {};
/** Narrower than set2’s 38% — keeps the photo compact on the leading edge. */
const EDGE_IMAGE_WIDTH_PCT = '34%' as const;
const CARD_MIN_HEIGHT = 112;

function buildCalorieRangeLabel(
  display: ReturnType<typeof resolveSubscriptionCardNutritionDisplay>,
): string | null {
  if (!display.show_calories_limit) {
    return null;
  }
  const min = display.fields.min_calories != null ? formatNumber(Math.round(display.fields.min_calories)) : null;
  const max = display.fields.max_calories != null ? formatNumber(Math.round(display.fields.max_calories)) : null;
  if (min != null && max != null) {
    return `${min} – ${max} kcal`;
  }
  if (min != null) {
    return `${min} kcal`;
  }
  if (max != null) {
    return `${max} kcal`;
  }
  return null;
}

function buildScaledOrPortionLine(
  display: ReturnType<typeof resolveSubscriptionCardNutritionDisplay>,
  t: (key: string, params?: Record<string, string | number>) => string,
  portion_text_mode: 'portion' | 'person',
): string | null {
  if (display.show_portion && display.fields.portion != null) {
    return formatPersonCountLabel(display.fields.portion, t, portion_text_mode);
  }
  if (!display.show_scaled_macros) {
    return null;
  }
  const parts: string[] = [];
  if (display.fields.scaled_protein != null) {
    parts.push(`${formatNumber(Math.round(display.fields.scaled_protein))}g P`);
  }
  if (display.fields.scaled_carbs != null) {
    parts.push(`${formatNumber(Math.round(display.fields.scaled_carbs))}g C`);
  }
  return parts.length > 0 ? parts.join('  ·  ') : null;
}

function EdgePlanImage({
  uri,
  colors,
  a11y_label,
}: {
  uri: string | null;
  colors: ThemeColors;
  a11y_label: string;
}) {
  const [image_error, set_image_error] = useState(false);
  const show_image = Boolean(uri) && !image_error;
  const tint = (hexToRgba(colors.primary, 0.16) as string) || colors.primaryFade;

  return (
    <View
      style={[styles.edgeImage, { backgroundColor: tint }]}
      accessible
      accessibilityLabel={a11y_label}
      accessibilityRole="image"
      pointerEvents="none">
      {show_image ? (
        <Image source={{ uri: uri! }} style={StyleSheet.absoluteFillObject} resizeMode="cover" onError={() => set_image_error(true)} />
      ) : (
        <View style={styles.edgeFallback}>
          <SvgIcon name="meal" size={28} color={colors.primary} />
        </View>
      )}
    </View>
  );
}

export interface DashboardSubscriptionCardSet3Props {
  colors: ThemeColors;
  subscription: SubscriptionData | null;
  subscription_loading: boolean;
  days_left: number;
  paused_days_left?: number;
  menu_fallback_items: FlatItem[];
  on_press: () => void;
}

export function DashboardSubscriptionCardSet3({
  colors,
  subscription,
  subscription_loading,
  days_left,
  paused_days_left = 0,
  menu_fallback_items,
  on_press,
}: DashboardSubscriptionCardSet3Props) {
  const { t } = useTranslation();
  const rtl = useRtl();
  const show_scaled_macros_gate = useShowSubscriptionScaledMacrosEnabled();
  const calories_limit_gate = useCaloriesLimitVisible();
  const show_portion_gate = useShowPortionEnabled();
  const portion_text_mode = usePortionTextMode();
  const text_align = rtl.textAlign;
  const writing_direction = rtl.writingDirection;

  const strip_uri = useMemo(() => {
    const with_uri = menu_fallback_items.filter(i => i.uri);
    return with_uri[0]?.uri ?? null;
  }, [menu_fallback_items]);

  const { data: catalog_plans } = useSubscriptionPlans();

  const image_uri = useMemo(() => {
    if (!subscription) {
      return strip_uri;
    }
    return resolveMergedSubscriptionImageUri(subscription, catalog_plans) ?? strip_uri;
  }, [subscription, catalog_plans, strip_uri]);

  const plan_title = subscription
    ? getSubscriptionPlanTitleFromMerged(subscription, t('dashboard.myPlan'))
    : t('dashboard.myPlan');

  const nutrition_display = useMemo(() => {
    if (!subscription) {
      return null;
    }
    const fields = resolvePurchasedSubscriptionCardNutrition(subscription);
    return resolveSubscriptionCardNutritionDisplay(fields, {
      show_scaled_macros: show_scaled_macros_gate,
      calories_limit: calories_limit_gate,
      show_portion: show_portion_gate,
    });
  }, [subscription, show_scaled_macros_gate, calories_limit_gate, show_portion_gate]);

  const meals_count = subscription?.detail?.total_meals ?? 0;
  const snacks_count = subscription?.detail?.total_snacks ?? 0;
  const meals_label = meals_count > 0 ? `${formatNumber(meals_count)} ${t('dashboard.mealsLabel')}` : null;
  const snacks_label = snacks_count > 0 ? `${formatNumber(snacks_count)} ${t('dashboard.snacksLabel')}` : null;

  const calorie_label = useMemo(() => {
    if (!nutrition_display) {
      return null;
    }
    return buildCalorieRangeLabel(nutrition_display);
  }, [nutrition_display]);

  const scaled_line = useMemo(() => {
    if (!nutrition_display) {
      return null;
    }
    return buildScaledOrPortionLine(nutrition_display, t, portion_text_mode);
  }, [nutrition_display, portion_text_mode, t]);

  const show_nutrition = Boolean(scaled_line || calorie_label);
  const days_remaining_label = formatMealDaysRemainingLabel(days_left, paused_days_left, t);
  const circle_bg = hexToRgba('#0F172A', 0.08) as string;
  const plain_meta_line = [meals_label, snacks_label, days_remaining_label].filter(Boolean).join(' · ');

  const render_meta_tags = () => (
    <View style={styles.tagsStack}>
      {(meals_label || snacks_label) ? (
        <View style={[styles.tagsCluster, rtl.flipRowStyle]}>
          {meals_label ? (
            <View
              style={[
                styles.metaTag,
                styles.metaTagFlex,
                {
                  backgroundColor: colors.primary,
                  borderColor: colors.primary,
                },
              ]}>
              <Text style={[styles.metaTagText, { color: colors.primaryText }, ANDROID_TEXT]} numberOfLines={1}>
                {meals_label}
              </Text>
            </View>
          ) : null}
          {snacks_label ? (
            <View
              style={[
                styles.metaTag,
                styles.metaTagFlex,
                {
                  backgroundColor: colors.primaryText,
                  borderColor: colors.primary,
                },
              ]}>
              <Text style={[styles.metaTagText, { color: colors.primary }, ANDROID_TEXT]} numberOfLines={1}>
                {snacks_label}
              </Text>
            </View>
          ) : null}
        </View>
      ) : null}
      <View
        style={[
          styles.metaTag,
          styles.daysTagFull,
          {
            backgroundColor: colors.tertiary,
            borderColor: colors.tertiary,
          },
        ]}>
        <Text style={[styles.metaTagText, { color: colors.tertiaryText }, ANDROID_TEXT]} numberOfLines={1}>
          {days_remaining_label}
        </Text>
      </View>
    </View>
  );

  const render_card = (hero_uri: string | null, body: React.ReactNode, a11y: string) => (
    <Clickable onPress={on_press} accessibilityLabel={a11y} accessibilityRole="button">
      <View style={[styles.cardRoot, dashboardSet1CardShell(colors)]}>
        <View style={[styles.shell, horizontalLtrDirectionStyle]}>
          <EdgePlanImage uri={hero_uri} colors={colors} a11y_label={a11y} />
          <View style={styles.body}>{body}</View>
        </View>
      </View>
    </Clickable>
  );

  if (subscription_loading && !subscription) {
    return render_card(
      null,
      <Text style={[styles.title, { color: colors.text, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}>
        {t('common.loading')}
      </Text>,
      t('dashboard.myPlan'),
    );
  }

  if (!subscription) {
    return render_card(
      image_uri,
      <>
        <View style={[styles.headerRow, rtl.flipRowStyle]}>
          <Text
            style={[styles.eyebrow, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
            numberOfLines={1}>
            {t('dashboard.myPlan')}
          </Text>
          <View style={[styles.chevronCircle, { backgroundColor: circle_bg }]}>
            <SvgIcon name={rtl.trailingRowDisclosureChevron} size={14} color={colors.textSecondary} />
          </View>
        </View>
        <Text
          style={[styles.emptyCopy, { color: colors.textSecondary, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
          numberOfLines={2}>
          {t('subscriptions.noActivePlans')}
        </Text>
        <Text style={[styles.cta, { color: colors.primary, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}>
          {t('dashboard.browsePlans')}
        </Text>
      </>,
      t('dashboard.browsePlans'),
    );
  }

  return render_card(
    image_uri,
    <>
      <View style={[styles.headerRow, rtl.flipRowStyle]}>
        <Text
          style={[styles.eyebrow, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
          numberOfLines={1}>
          {t('dashboard.myPlan')}
        </Text>
        <View style={[styles.chevronCircle, { backgroundColor: circle_bg }]}>
          <SvgIcon name={rtl.trailingRowDisclosureChevron} size={14} color={colors.textSecondary} />
        </View>
      </View>

      <Text
        style={[styles.title, { color: colors.text, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
        numberOfLines={1}>
        {plan_title}
      </Text>

      {show_nutrition ? (
        <View style={[styles.columnsRow, rtl.flipRowStyle]}>
          <View style={styles.nutritionBlock}>
            {scaled_line ? (
              <Text
                style={[styles.scaled, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
                numberOfLines={1}>
                {scaled_line}
              </Text>
            ) : null}
            {calorie_label ? (
              <Text
                style={[styles.calorieEnd, { color: colors.textSecondary, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
                numberOfLines={1}>
                {calorie_label}
              </Text>
            ) : null}
          </View>
          {render_meta_tags()}
        </View>
      ) : plain_meta_line ? (
        <Text
          style={[styles.plainMeta, { color: colors.textSecondary, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
          numberOfLines={1}>
          {plain_meta_line}
        </Text>
      ) : null}
    </>,
    plan_title,
  );
}

const styles = StyleSheet.create({
  cardRoot: {
    borderRadius: DASH_STUDIO_RADIUS,
    overflow: 'hidden',
  },
  shell: {
    width: '100%',
    minWidth: 0,
    minHeight: CARD_MIN_HEIGHT,
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  edgeImage: {
    width: EDGE_IMAGE_WIDTH_PCT,
    alignSelf: 'stretch',
    overflow: 'hidden',
    flexShrink: 0,
    position: 'relative',
    /** Card `overflow: hidden` clips the leading corners flush to the shell. */
  },
  edgeFallback: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flex: 1,
    minWidth: 0,
    paddingStart: appTheme.spacing.md,
    paddingEnd: DASH_STUDIO_INNER,
    paddingVertical: appTheme.spacing.sm + 2,
    gap: 6,
    justifyContent: 'center',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: appTheme.spacing.xs,
    minWidth: 0,
  },
  eyebrow: {
    flex: 1,
    minWidth: 0,
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.85,
    textTransform: 'uppercase',
  },
  chevronCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  title: {
    fontSize: 16,
    fontWeight: '800',
    lineHeight: 20,
    letterSpacing: -0.3,
  },
  columnsRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    gap: appTheme.spacing.sm,
  },
  nutritionBlock: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'center',
    gap: 2,
  },
  scaled: {
    fontSize: 13,
    lineHeight: 17,
    fontWeight: '700',
  },
  calorieEnd: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '600',
  },
  tagsStack: {
    flexShrink: 0,
    maxWidth: '58%',
    minWidth: 128,
    gap: 5,
  },
  tagsCluster: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  metaTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth * 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  metaTagFlex: {
    flex: 1,
    minWidth: 0,
  },
  daysTagFull: {
    width: '100%',
  },
  metaTagText: {
    fontSize: 11,
    fontWeight: '700',
    lineHeight: 13,
    textAlign: 'center',
  },
  plainMeta: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '600',
  },
  emptyCopy: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '500',
  },
  cta: {
    fontSize: 13,
    fontWeight: '700',
  },
});
